import type React from "react";
import {memo, useCallback, useEffect, useRef} from "react";
import type {NativeSyntheticEvent, NativeTouchEvent, Text as RNText} from "react-native";

import {CloseIcon} from "../../react/components/Icons/CloseIcon";
import {useEllipsisVisible} from "../../react/hooks/useEllipsisVisible";
import {Badge, BadgeIcon, BadgeText} from "../ui/badge";
import {Pressable} from "../ui/pressable";
import {Tooltip} from "../ui/tooltip";

export type Props = {
    isInvalid?: boolean;
    children: string;
    onClose?: () => void;
    onPress?: () => void;
};

const Tag: React.FC<Props> = ({isInvalid, children, onClose, onPress, ...props}) => {
    const [isEllipsisVisible, ellipsisVisibleRef] = useEllipsisVisible();
    const textRef = useRef<React.ElementRef<typeof RNText>>(null);

    const handleClose = useCallback(
        (event: NativeSyntheticEvent<NativeTouchEvent>) => {
            event.stopPropagation();
            onClose?.();
        },
        [onClose]
    );

    useEffect(() => {
        if (process.env.IS_WEB && textRef.current && textRef.current instanceof HTMLDivElement) {
            ellipsisVisibleRef(textRef.current);
        }
    }, [ellipsisVisibleRef]);

    let badgeNode = (
        <Badge
            action={isInvalid ? "error" : "default"}
            className="max-w-[10rem]"
            variant={isInvalid ? "outline" : "subtle"}
            {...props}
        >
            <BadgeText ref={textRef} isTruncated>
                {children}
            </BadgeText>
            {onClose && (
                <Pressable onPress={handleClose}>
                    <BadgeIcon as={CloseIcon} />
                </Pressable>
            )}
        </Badge>
    );

    badgeNode = onPress ? <Pressable onPress={onPress}>{badgeNode}</Pressable> : badgeNode;

    if (isEllipsisVisible) {
        return <Tooltip label={children}>{badgeNode}</Tooltip>;
    }

    return badgeNode;
};

export default memo(Tag);
